import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  FaArrowLeft, 
  FaEdit, 
  FaEye, 
  FaToggleOn, 
  FaToggleOff,
  FaPlus,
  FaSearch,
  FaFilter,
  FaCog
} from 'react-icons/fa';
import { useNotification } from '../context/NotificationContext';
import TemplateEditor from '../components/Notifications/TemplateEditor';
import { CATEGORY_METADATA } from '../api/notifications';
import styles from './TemplateManagementPage.module.css';

/**
 * TemplateManagementPage Component
 * 
 * Comprehensive template management interface with:
 * - Template listing by category
 * - Template editing
 * - Template preview
 * - Enable/disable templates
 * - Search and filtering
 */
const TemplateManagementPage = () => {
  const [templates, setTemplates] = useState([]);
  const [filteredTemplates, setFilteredTemplates] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [showEditor, setShowEditor] = useState(false);

  const navigate = useNavigate();
  const { clubId } = useParams();
  const { addNotification } = useNotification();

  // Mock template data
  const mockTemplates = [
    {
      _id: 'template_club_join_request',
      name: 'Club Join Request',
      type: 'club_join_request',
      category: 'club',
      isActive: true,
      isSystemDefault: true,
      hasCustomizations: false,
      lastModified: new Date('2024-01-15'),
      usage: { totalSent: 45, deliveryRate: 98.2 }
    },
    {
      _id: 'template_club_join_approved',
      name: 'Club Join Approved',
      type: 'club_join_approved',
      category: 'club',
      isActive: true,
      isSystemDefault: false,
      hasCustomizations: true,
      lastModified: new Date('2024-01-10'),
      usage: { totalSent: 38, deliveryRate: 100 }
    },
    {
      _id: 'template_program_registration_submitted',
      name: 'Program Registration Submitted',
      type: 'program_registration_submitted',
      category: 'program',
      isActive: true,
      isSystemDefault: true,
      hasCustomizations: false,
      lastModified: new Date('2024-01-12'),
      usage: { totalSent: 156, deliveryRate: 97.4 }
    },
    {
      _id: 'template_event_created',
      name: 'Event Created',
      type: 'event_created',
      category: 'event',
      isActive: false,
      isSystemDefault: true,
      hasCustomizations: false,
      lastModified: new Date('2024-01-08'),
      usage: { totalSent: 23, deliveryRate: 95.7 }
    },
    {
      _id: 'template_rsvp_reminder',
      name: 'RSVP Reminder',
      type: 'rsvp_reminder',
      category: 'rsvp',
      isActive: true,
      isSystemDefault: false,
      hasCustomizations: true,
      lastModified: new Date('2024-01-14'),
      usage: { totalSent: 89, deliveryRate: 96.6 }
    },
    {
      _id: 'template_equipment_assigned',
      name: 'Equipment Assigned',
      type: 'equipment_assigned',
      category: 'equipment',
      isActive: true,
      isSystemDefault: true,
      hasCustomizations: false,
      lastModified: new Date('2024-01-11'),
      usage: { totalSent: 12, deliveryRate: 100 }
    }
  ];

  /**
   * Load templates
   */
  useEffect(() => {
    const loadTemplates = async () => {
      setLoading(true);
      try {
        // In a real implementation, this would fetch from the API
        await new Promise(resolve => setTimeout(resolve, 1000)); // Mock delay
        setTemplates(mockTemplates);
        setFilteredTemplates(mockTemplates);
      } catch (error) {
        console.error('Error loading templates:', error);
        addNotification({ message: 'Failed to load templates', type: 'error' });
      } finally {
        setLoading(false);
      }
    };

    loadTemplates();
  }, [clubId]);

  /**
   * Filter templates
   */
  useEffect(() => {
    let filtered = templates;

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(template => template.category === selectedCategory);
    }

    // Filter by search term
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(template => 
        template.name.toLowerCase().includes(term) ||
        template.type.toLowerCase().includes(term) ||
        template.category.toLowerCase().includes(term)
      );
    }

    setFilteredTemplates(filtered);
  }, [templates, selectedCategory, searchTerm]);

  /**
   * Handle template toggle
   */
  const handleToggleTemplate = async (templateId, isActive) => {
    try {
      // In a real implementation, this would call the API
      setTemplates(prev => prev.map(template => 
        template._id === templateId 
          ? { ...template, isActive: !isActive }
          : template
      ));
      
      addNotification({ 
        message: `Template ${!isActive ? 'enabled' : 'disabled'} successfully`, 
        type: 'success' 
      });
    } catch (error) {
      console.error('Error toggling template:', error);
      addNotification({ message: 'Failed to update template', type: 'error' });
    }
  };

  /**
   * Handle edit template
   */
  const handleEditTemplate = (template) => {
    setEditingTemplate(template);
    setShowEditor(true);
  };

  /**
   * Handle save template
   */
  const handleSaveTemplate = (updatedTemplate) => {
    setTemplates(prev => prev.map(template => 
      template._id === updatedTemplate._id 
        ? { ...template, ...updatedTemplate, hasCustomizations: true, lastModified: new Date() }
        : template
    ));
    
    setShowEditor(false);
    setEditingTemplate(null);
    addNotification({ message: 'Template saved successfully', type: 'success' });
  };

  /**
   * Handle cancel edit
   */
  const handleCancelEdit = () => {
    setShowEditor(false);
    setEditingTemplate(null);
  };

  /**
   * Get category options
   */
  const categoryOptions = [
    { key: 'all', name: 'All Templates', icon: '📋' },
    ...Object.entries(CATEGORY_METADATA).map(([key, meta]) => ({
      key,
      name: meta.name,
      icon: meta.icon
    }))
  ];

  /**
   * Get template stats
   */
  const getTemplateStats = () => {
    const total = templates.length;
    const active = templates.filter(t => t.isActive).length;
    const customized = templates.filter(t => t.hasCustomizations).length;
    const avgDeliveryRate = templates.reduce((acc, t) => acc + (t.usage?.deliveryRate || 0), 0) / total;

    return { total, active, customized, avgDeliveryRate: avgDeliveryRate.toFixed(1) };
  };

  if (showEditor) {
    return (
      <TemplateEditor
        clubId={clubId}
        templateType={editingTemplate?.type}
        initialTemplate={editingTemplate}
        onSave={handleSaveTemplate}
        onCancel={handleCancelEdit}
      />
    );
  }

  const stats = getTemplateStats();

  return (
    <div className={styles.templateManagementPage}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <button
            className={styles.backButton}
            onClick={() => navigate(-1)}
            aria-label="Go back"
          >
            <FaArrowLeft />
          </button>
          <div className={styles.titleSection}>
            <h1 className={styles.pageTitle}>
              <FaCog className={styles.titleIcon} />
              Notification Templates
            </h1>
            <p className={styles.subtitle}>
              Customize notification templates for your club
            </p>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className={styles.statsSection}>
        <div className={styles.statCard}>
          <div className={styles.statValue}>{stats.total}</div>
          <div className={styles.statLabel}>Total Templates</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statValue}>{stats.active}</div>
          <div className={styles.statLabel}>Active Templates</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statValue}>{stats.customized}</div>
          <div className={styles.statLabel}>Customized</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statValue}>{stats.avgDeliveryRate}%</div>
          <div className={styles.statLabel}>Avg Delivery Rate</div>
        </div>
      </div>

      {/* Filters */}
      <div className={styles.filtersSection}>
        <div className={styles.searchBox}>
          <FaSearch className={styles.searchIcon} />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search templates..."
            className={styles.searchInput}
          />
        </div>

        <div className={styles.categoryFilters}>
          {categoryOptions.map(category => (
            <button
              key={category.key}
              className={`${styles.categoryFilter} ${selectedCategory === category.key ? styles.active : ''}`}
              onClick={() => setSelectedCategory(category.key)}
            >
              <span className={styles.categoryIcon}>{category.icon}</span>
              <span className={styles.categoryName}>{category.name}</span>
              <span className={styles.categoryCount}>
                {category.key === 'all' 
                  ? templates.length 
                  : templates.filter(t => t.category === category.key).length
                }
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Templates List */}
      <div className={styles.templatesSection}>
        {loading ? (
          <div className={styles.loading}>
            <div className={styles.spinner}>⏳</div>
            <p>Loading templates...</p>
          </div>
        ) : filteredTemplates.length === 0 ? (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}>📝</div>
            <h3>No Templates Found</h3>
            <p>
              {searchTerm ? 
                `No templates match "${searchTerm}"` : 
                `No templates in ${selectedCategory === 'all' ? 'any category' : selectedCategory}`
              }
            </p>
          </div>
        ) : (
          <div className={styles.templateGrid}>
            {filteredTemplates.map(template => (
              <TemplateCard
                key={template._id}
                template={template}
                onEdit={() => handleEditTemplate(template)}
                onToggle={() => handleToggleTemplate(template._id, template.isActive)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Template Card Component
 */
const TemplateCard = ({ template, onEdit, onToggle }) => {
  const categoryMeta = CATEGORY_METADATA[template.category];
  
  return (
    <div className={`${styles.templateCard} ${!template.isActive ? styles.disabled : ''}`}>
      {/* Header */}
      <div className={styles.cardHeader}>
        <div className={styles.cardTitle}>
          <span className={styles.cardIcon}>
            {categoryMeta?.icon || '📝'}
          </span>
          <div>
            <h3 className={styles.templateName}>{template.name}</h3>
            <p className={styles.templateType}>{template.type.replace(/_/g, ' ')}</p>
          </div>
        </div>
        
        <div className={styles.cardActions}>
          <button
            onClick={onToggle}
            className={styles.toggleButton}
            title={template.isActive ? 'Disable template' : 'Enable template'}
          >
            {template.isActive ? (
              <FaToggleOn className={styles.toggleOn} />
            ) : (
              <FaToggleOff className={styles.toggleOff} />
            )}
          </button>
        </div>
      </div>

      {/* Content */}
      <div className={styles.cardContent}>
        <div className={styles.templateMeta}>
          <div className={styles.metaItem}>
            <span className={styles.metaLabel}>Category:</span>
            <span className={styles.metaValue}>{categoryMeta?.name || template.category}</span>
          </div>
          <div className={styles.metaItem}>
            <span className={styles.metaLabel}>Status:</span>
            <span className={`${styles.metaValue} ${styles.status} ${template.isActive ? styles.active : styles.inactive}`}>
              {template.isActive ? 'Active' : 'Disabled'}
            </span>
          </div>
          <div className={styles.metaItem}>
            <span className={styles.metaLabel}>Type:</span>
            <span className={styles.metaValue}>
              {template.isSystemDefault ? (
                template.hasCustomizations ? (
                  <span className={styles.customized}>Customized</span>
                ) : (
                  <span className={styles.default}>System Default</span>
                )
              ) : (
                <span className={styles.custom}>Custom</span>
              )}
            </span>
          </div>
        </div>

        {/* Usage Stats */}
        {template.usage && (
          <div className={styles.usageStats}>
            <div className={styles.usageStat}>
              <span className={styles.usageValue}>{template.usage.totalSent}</span>
              <span className={styles.usageLabel}>Sent</span>
            </div>
            <div className={styles.usageStat}>
              <span className={styles.usageValue}>{template.usage.deliveryRate}%</span>
              <span className={styles.usageLabel}>Delivered</span>
            </div>
          </div>
        )}

        {/* Last Modified */}
        <div className={styles.lastModified}>
          Last modified: {template.lastModified.toLocaleDateString()}
        </div>
      </div>

      {/* Footer */}
      <div className={styles.cardFooter}>
        <button
          onClick={onEdit}
          className={styles.editButton}
          disabled={!template.isActive}
        >
          <FaEdit /> Edit Template
        </button>
      </div>
    </div>
  );
};

export default TemplateManagementPage;
